import { makeAutoObservable } from 'mobx';
import {
  IAuthorizationService,
  YANDEX_CLOUD_BLOCKING_ERRORS,
  YANDEX_CLOUD_ERROR_KEY,
  type YandexCloudError,
} from '../../../../../infrastructure/types/features/auth/auth-types';
import { YandexCloudData } from '../../../../../infrastructure/types/features/auth/auth-contracts';

class YandexCloudManager {
  isActive = false;
  token?: string;
  tokenError?: YandexCloudError;
  loginError?: YandexCloudError;
  registrationError?: YandexCloudError;

  constructor(private authService: IAuthorizationService) {
    makeAutoObservable(this, {});
    this.initialize();
  }

  get data(): YandexCloudData | undefined {
    if (!this.token || this.isBlocked) {
      return undefined;
    }

    return { token: this.token };
  }

  get isBlocked(): boolean {
    return !!this.blockingError;
  }

  get hasTransition(): boolean {
    return this.isActive || !!this.loginError || !!this.registrationError;
  }

  get loginErrorKey(): string | undefined {
    return this.getErrorKey(this.tokenError ?? this.loginError ?? this.blockingError);
  }

  get registrationErrorKey(): string | undefined {
    return this.getErrorKey(this.tokenError ?? this.registrationError ?? this.blockingError);
  }

  get showLoginInstructions(): boolean {
    return (
      this.isActive &&
      !this.tokenError &&
      !this.blockingError &&
      (!this.loginError || this.loginError === 'not_yandex_cloud_account')
    );
  }

  setLoginError(error?: string): boolean {
    const yandexCloudError = this.toYandexCloudError(error);
    if (!yandexCloudError) {
      return false;
    }

    this.loginError = yandexCloudError;
    return true;
  }

  setRegistrationError(error?: string): boolean {
    const yandexCloudError = this.toYandexCloudError(error);
    if (!yandexCloudError) {
      return false;
    }

    this.registrationError = yandexCloudError;
    return true;
  }

  clearLoginError() {
    if (!this.loginError || YANDEX_CLOUD_BLOCKING_ERRORS.includes(this.loginError)) {
      return;
    }

    this.loginError = undefined;
  }

  clearRegistrationError() {
    if (!this.registrationError || YANDEX_CLOUD_BLOCKING_ERRORS.includes(this.registrationError)) {
      return;
    }

    this.registrationError = undefined;
  }

  clear() {
    this.authService.clearYandexCloudTransition();
    this.isActive = false;
    this.token = undefined;
    this.tokenError = undefined;
    this.loginError = undefined;
    this.registrationError = undefined;
  }

  private initialize() {
    const transition = this.authService.consumeYandexCloudTransition();
    this.isActive = transition.isActive;
    this.token = transition.token;
    this.refreshValidation();

    if (this.tokenError) {
      this.authService.clearYandexCloudTransition();
      if (!transition.isFromURL) {
        this.isActive = false;
        this.token = undefined;
        this.tokenError = undefined;
      }
    } else if (this.isActive && this.token) {
      this.authService.saveYandexCloudTransition(this.token);
    }

    this.setLoginError(transition.authError);
  }

  refreshValidation(): boolean {
    if (!this.isActive) {
      this.tokenError = undefined;
    } else if (!this.token) {
      this.tokenError = 'missing_token';
    } else {
      this.tokenError = this.validateToken(this.token);
    }

    return !this.isBlocked;
  }

  private validateToken(token: string): YandexCloudError | undefined {
    try {
      const parts = token.split('.');
      if (parts.length !== 3 || !parts[1]) {
        return 'invalid_token';
      }

      const payload = this.decodePayload(parts[1]);
      if (typeof payload.exp !== 'number' || !Number.isInteger(payload.exp)) {
        return 'missing_expiration';
      }

      if (payload.exp <= Math.floor(Date.now() / 1000)) {
        return 'expired_token';
      }

      return undefined;
    } catch {
      return 'invalid_token';
    }
  }

  private decodePayload(value: string): { exp?: unknown } {
    const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
    const remainder = base64.length % 4;
    const padded = remainder === 0 ? base64 : base64 + '='.repeat(4 - remainder);
    return JSON.parse(atob(padded));
  }

  private toYandexCloudError(error?: string): YandexCloudError | undefined {
    if (!error || !Object.prototype.hasOwnProperty.call(YANDEX_CLOUD_ERROR_KEY, error)) {
      return undefined;
    }

    return error as YandexCloudError;
  }

  private getErrorKey(error?: YandexCloudError): string | undefined {
    return error ? YANDEX_CLOUD_ERROR_KEY[error] : undefined;
  }

  private get blockingError(): YandexCloudError | undefined {
    return [this.tokenError, this.loginError, this.registrationError].find(
      (error): error is YandexCloudError => !!error && YANDEX_CLOUD_BLOCKING_ERRORS.includes(error),
    );
  }
}

export { YandexCloudManager };
